/**
 * The LineTool's smart-snap pass — shared default behavior for both
 * `ManualLineMode` and `AutomaticLineMode`, which run the same drag pipeline
 * (`PromptDragHandler.movePromptToMouse` → constraint snap → recompute screen
 * position → refresh preview).
 *
 * The snap itself (the `LineConstraintSnap` engine, the Ctrl-to-disable gate,
 * the unit-aware quantization grid + distance label, and the shared dashed-line
 * indicator) lives HERE, as a default of the tool. A kind only participates by:
 *   - opting in via `kindAdapter.enableConstraintSnap`, and
 *   - supplying the reference geometry to snap against via
 *     `kindAdapter.getConstraintReferences(...)`.
 * Adapters carry no snap math and know nothing about display units.
 *
 * Centralizing this keeps the two modes from duplicating the guard, the engine
 * call, the screen-position recompute, and the visual refresh.
 */

import * as THREE from 'three';
import type { Viewer } from '../../../../viewer/viewer';
import type { AnnotationKindAdapter } from '../../implementations/line-tool/AnnotationKindAdapter';
import type { EditableShape } from '../../implementations/line-tool/EditableShape';
import type { BasePlaneState } from '../../implementations/line-tool/SelectionContextProvider';
import type { CoordinateTransformer } from './../utils/CoordinateTransformer';
import type { LinePrompt } from './types';
import type { ConstraintSnapVisuals } from './ConstraintSnapVisuals';
import { LineConstraintSnap } from './LineConstraintSnap';

/**
 * Shared snap engine. Stateless apart from `maxRange` config, so a single
 * module-level instance serves every kind and both line modes.
 */
const constraintSnap = new LineConstraintSnap();

export interface ConstraintSnapContext {
  viewer: Viewer;
  kindAdapter: AnnotationKindAdapter;
  basePlane: BasePlaneState | null;
  editingShape: EditableShape | null;
  coordinateTransformer: CoordinateTransformer;
  modifiers: { shiftDown: boolean; ctrlDown: boolean };
  /**
   * Live prompt list — passed through to the adapter so it can reconstruct
   * the in-progress shape geometry (e.g., the outline ring as currently
   * edited) for self-snap rejection.
   */
  allPrompts: ReadonlyArray<LinePrompt>;
  /** Shared indicator. Null disables the visual but keeps the snap behavior. */
  visuals: ConstraintSnapVisuals | null;
}

/**
 * Run the LineTool's default constraint snap for one prompt. Gated on the
 * kind opting in (`enableConstraintSnap`); skipped while Ctrl is held (free
 * placement) or with no base plane. The kind supplies only the reference
 * geometry via `getConstraintReferences`; the snap math, unit-aware grid +
 * label, point rewrite, and indicator are all handled here.
 *
 * When a snap fires, rewrite the prompt's world point, refresh its cached
 * screen position, and show the dashed-line + distance-label indicator. When
 * no snap fires, clear the indicator.
 *
 * Returns true when the snap was applied (so the caller can decide whether
 * to re-render the shape preview); false when skipped.
 */
export function applyConstraintSnap(prompt: LinePrompt, ctx: ConstraintSnapContext): boolean {
  if (!ctx.kindAdapter.enableConstraintSnap) return false;
  const getReferences = ctx.kindAdapter.getConstraintReferences;
  if (!getReferences) return false;

  // Ctrl disables snapping for free placement; no base plane → nothing to snap on.
  if (ctx.modifiers.ctrlDown || !ctx.basePlane) {
    ctx.visuals?.clear();
    return false;
  }

  const references = getReferences.call(ctx.kindAdapter, {
    viewer: ctx.viewer,
    basePlane: ctx.basePlane,
    shape: ctx.editingShape,
    prompt,
    allPrompts: ctx.allPrompts,
  });
  if (references.length === 0) {
    ctx.visuals?.clear();
    return false;
  }

  const candidate = prompt.worldPoint.clone();
  // Drag axis = perpendicular of the prompt's line direction in XY.
  // The two perpendicular rays the snap engine casts run along this axis.
  const promptDirXY = new THREE.Vector2(prompt.direction.x, prompt.direction.y);
  const dragAxis = new THREE.Vector2(-promptDirXY.y, promptDirXY.x);
  if (dragAxis.lengthSq() < 1e-12) {
    ctx.visuals?.clear();
    return false;
  }
  dragAxis.normalize();

  // Display unit is global (the user's preference), not per-adapter — resolve
  // it here so the snap grid + label are unit-aware for every kind.
  const result = constraintSnap.resolve(candidate, references, { dragAxis, unit: ctx.viewer.lengthUnitDisplay });
  if (!result) {
    ctx.visuals?.clear();
    return false;
  }

  const planeZ = ctx.basePlane?.origin.z ?? prompt.worldPoint.z;
  prompt.worldPoint.set(result.adjusted.x, result.adjusted.y, planeZ);
  prompt.screenPosition = ctx.coordinateTransformer.worldToScreen(prompt.worldPoint);
  ctx.visuals?.show(candidate, result, planeZ);
  return true;
}

/** Convenience: clear the shared indicator (e.g. on drag end or mode deactivate). */
export function clearConstraintSnapVisuals(visuals: ConstraintSnapVisuals | null): void {
  visuals?.clear();
}
